
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { YorubaReaction, YorubaReactionType, YORUBA_REACTIONS } from './types';
import { toast } from 'sonner';

interface YorubaReactionsProps {
  targetType: 'topic' | 'reply' | 'comment' | 'chat';
  targetId: string;
  reactions: YorubaReaction[];
  onReactionUpdate: (reactions: YorubaReaction[]) => void;
}

export default function YorubaReactions({ 
  targetType, 
  targetId, 
  reactions = [], 
  onReactionUpdate 
}: YorubaReactionsProps) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Group reactions by type and count
  const reactionCounts = reactions.reduce((acc, reaction) => {
    acc[reaction.reaction_type] = (acc[reaction.reaction_type] || 0) + 1;
    return acc;
  }, {} as Record<YorubaReactionType, number>);

  // Check if current user has reacted
  const userReactions = reactions.filter(r => r.user_id === user?.id);
  const hasUserReacted = (type: YorubaReactionType) => 
    userReactions.some(r => r.reaction_type === type);

  const handleReaction = async (reactionType: YorubaReactionType) => {
    if (!user) {
      toast.error('Please log in to react');
      return;
    }

    setLoading(true);
    try {
      const existingReaction = userReactions.find(r => r.reaction_type === reactionType);
      
      if (existingReaction) {
        // Remove reaction
        const { error } = await supabase
          .from('yoruba_reactions')
          .delete()
          .eq('id', existingReaction.id);
          
        if (error) throw error;
        
        const newReactions = reactions.filter(r => r.id !== existingReaction.id);
        onReactionUpdate(newReactions);
      } else {
        // Add reaction
        const { data, error } = await supabase
          .from('yoruba_reactions')
          .insert({
            user_id: user.id,
            target_type: targetType,
            target_id: targetId,
            reaction_type: reactionType
          })
          .select()
          .single();
          
        if (error) throw error;
        
        // Properly type the response
        const newReaction: YorubaReaction = {
          ...data,
          target_type: data.target_type as 'topic' | 'reply' | 'comment' | 'chat',
          reaction_type: data.reaction_type as YorubaReactionType
        };
        
        onReactionUpdate([...reactions, newReaction]);
      }
      
      setIsOpen(false);
    } catch (error) {
      console.error('Error handling reaction:', error);
      toast.error('Failed to update reaction');
    } finally {
      setLoading(false);
    }
  };

  // Show most popular reactions
  const popularReactions = Object.entries(reactionCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 3);

  return (
    <div className="flex items-center gap-2">
      {/* Show popular reactions */}
      {popularReactions.map(([type, count]) => {
        const reactionType = type as YorubaReactionType;
        const reaction = YORUBA_REACTIONS[reactionType];
        const isUserReacted = hasUserReacted(reactionType);
        
        return (
          <Button
            key={type}
            variant={isUserReacted ? "default" : "outline"}
            size="sm"
            className={`text-xs h-7 ${isUserReacted ? 'bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90' : ''}`}
            onClick={() => handleReaction(reactionType)}
            disabled={loading}
          >
            <span className="mr-1">{reaction.emoji}</span>
            {count}
          </Button>
        );
      })}

      {/* Add reaction button */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 px-2">
            <span>😊</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(YORUBA_REACTIONS).map(([type, reaction]) => {
              const reactionType = type as YorubaReactionType;
              const isSelected = hasUserReacted(reactionType);
              
              return (
                <Button
                  key={type}
                  variant={isSelected ? "default" : "ghost"}
                  size="sm"
                  className={`flex flex-col items-center p-3 h-auto ${
                    isSelected ? 'bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90' : ''
                  }`}
                  onClick={() => handleReaction(reactionType)}
                  disabled={loading}
                >
                  <span className="text-lg mb-1">{reaction.emoji}</span>
                  <span className="text-xs">{reaction.label}</span>
                </Button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
